import { CaretDownOutlined, CaretUpOutlined, CloudServerOutlined } from "@ant-design/icons";
import { EVisionAuthType, IApiGatewayConfig } from "@kuika/kuika-cl-model";
import { memo, useState } from "react";
import { Handle, Position, useReactFlow } from "reactflow";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import baseStyles from "./BaseNode.module.css";

interface ApiGatewayNodeData {
  type: "ApiGateway";
  title: string;
  config?: IApiGatewayConfig;
  status?: "idle" | "starting" | "active";
}

interface ApiGatewayNodeProps {
  data: ApiGatewayNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  service: {
    primaryColor: "#7c3aed",
    headerBackground: "rgba(124, 58, 237, 0.1)",
    iconColor: "#7c3aed",
    handleColor: "#7c3aed"
  }
};

const ApiGatewayNode = ({ data, id, selected }: ApiGatewayNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(data.status || "idle");
  const [config, setConfig] = useState<IApiGatewayConfig>(() => {
    // Default config values
    const defaults: IApiGatewayConfig = {
      endpoints: [],
      websocketEnabled: true,
      corsEnabled: true,
      authentication: EVisionAuthType.None
    };
    // Merge with provided config from template
    if (data.config) {
      return { ...defaults, ...data.config };
    }
    return defaults;
  });
  const { setNodes } = useReactFlow();

  const categoryStyles = NODE_CATEGORY_STYLES.service;

  const updateConfig = (updates: Partial<IApiGatewayConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.apiGateway.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {config.endpoints?.length || 0} endpoints • {status}
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <CloudServerOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.apiGateway.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Authentication</label>
              <select
                className={`${baseStyles.formSelect} ${baseStyles.nodrag}`}
                value={config.authentication}
                onChange={(e) => updateConfig({ authentication: e.target.value as EVisionAuthType })}
              >
                <option value={EVisionAuthType.None}>None</option>
                <option value={EVisionAuthType.Basic}>Basic Auth</option>
                <option value={EVisionAuthType.OAuth}>OAuth</option>
              </select>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.websocketEnabled || false}
                  onChange={(e) => updateConfig({ websocketEnabled: e.target.checked })}
                  className={baseStyles.nodrag}
                />
                WebSocket Enabled
              </label>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.corsEnabled || false}
                  onChange={(e) => updateConfig({ corsEnabled: e.target.checked })}
                  className={baseStyles.nodrag}
                />
                CORS Enabled
              </label>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Endpoints ({config.endpoints?.length || 0})</label>
              {config.endpoints && config.endpoints.length > 0 ? (
                <div className={baseStyles.itemList}>
                  {config.endpoints.map((endpoint, index) => (
                    <div key={index} className={baseStyles.endpointCard}>
                      <span
                        className={`${baseStyles.methodBadge} ${
                          endpoint.method === "GET"
                            ? baseStyles.methodGet
                            : endpoint.method === "POST"
                              ? baseStyles.methodPost
                              : endpoint.method === "PUT"
                                ? baseStyles.methodPut
                                : endpoint.method === "DELETE"
                                  ? baseStyles.methodDelete
                                  : baseStyles.methodDefault
                        }`}
                      >
                        {endpoint.method}
                      </span>
                      <span className={baseStyles.endpointPath}>{endpoint.path}</span>
                      {endpoint.dataSource && (
                        <span className={baseStyles.endpointDataSource}>→ {endpoint.dataSource}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <span className={`${baseStyles.collapsedSummary} ${baseStyles.emptyState}`}>
                  No endpoints configured
                </span>
              )}
            </div>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => alert("Endpoint configuration would open in a modal")}
            >
              Configure Endpoints
            </button>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => setStatus(status === "active" ? "idle" : "active")}
            >
              {status === "active" ? "Stop" : "Start"} Gateway
            </button>
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />
    </div>
  );
};

export default memo(ApiGatewayNode);
